// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import React from 'react';
import type { ReactNode } from 'react';
import './StatTile.css';

export interface StatTileProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Small uppercase-style label above the value (e.g. "Providers"). */
  label: ReactNode;
  /** The metric. null/undefined renders an em-dash. */
  value?: ReactNode;
  /** Optional secondary line under the value (e.g. a relative time). */
  subText?: ReactNode;
  /** Optional trailing element beside the value (e.g. a status Badge). */
  badge?: ReactNode;
  align?: 'left' | 'center';
}

/**
 * Metric tile for "stats header" rows above tables/dashboards (Advisor 2.0
 * tiles-over-table composition). Compose several inside a StatTileRow.
 */
export function StatTile({
  label,
  value,
  subText,
  badge,
  align = 'left',
  className = '',
  ...rest
}: StatTileProps) {
  const classes = ['fin-stat-tile', `fin-stat-tile--${align}`, className].filter(Boolean).join(' ');

  return (
    <div className={classes} {...rest}>
      <span className="fin-stat-tile__label">{label}</span>
      <span className="fin-stat-tile__value-row">
        <span className="fin-stat-tile__value" data-testid="stat-tile-value">
          {value === null || value === undefined || value === '' ? '—' : value}
        </span>
        {badge != null && <span className="fin-stat-tile__badge">{badge}</span>}
      </span>
      {subText != null && <span className="fin-stat-tile__subtext">{subText}</span>}
    </div>
  );
}

export interface StatTileRowProps extends React.HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  /** Render the row inside a white card (default true). */
  card?: boolean;
}

/** Equal-width flex row of StatTiles, optionally wrapped in the card idiom. */
export function StatTileRow({ children, card = true, className = '', ...rest }: StatTileRowProps) {
  const classes = ['fin-stat-tile-row', card && 'fin-stat-tile-row--card', className]
    .filter(Boolean)
    .join(' ');
  return (
    <div className={classes} {...rest}>
      {children}
    </div>
  );
}
